// src/services/messageActions.js
//
// 🟢 DELETE — the app's protocol, byte for byte, because both clients read the result.
//
//   for me        message doc gains `deletedFor: arrayUnion(myUid)` (an ARRAY here —
//                 the chat doc reuses the name as a map; chats.isDeletedFor knows both).
//                 Nobody is notified: it is a private filter, not an event.
//
//   for everyone  the doc is REWRITTEN IN PLACE, never deleted — a deleted doc would
//                 just vanish from the other side's snapshot with no explanation, while
//                 the tombstone renders as "Message deleted" everywhere. Then one socket
//                 emit ('delete-message'): the server flips open apps live AND fires the
//                 silent FCM that pulls the notification back off the recipient's lock
//                 screen (server/functions/index.js:3847 → sendFcmDeleteSignal). The
//                 web does not talk to FCM; the emit is what makes that happen.
//
// Eligibility for "everyone" (ChatModal.tsx:28245): my message, the recipient has NOT
// seen it, and it isn't a call record. No time window — seen state is the whole rule.

import {
  doc, updateDoc, setDoc, arrayUnion, deleteDoc, deleteField, serverTimestamp, increment,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { getIdentity } from '../crypto/identity';
import { albumItemRemoval, albumEmptyFor } from './mediaRuns';
import { reactionTargetKind } from './chats';
import { quoteTextFor } from './replies';
import { getSocket } from './socket';
import { deriveReceiptStatus } from '../ui/MessageTicks';

/** May THIS message be deleted for everyone? The app's rule — mine, unseen, not a call. */
export function canDeleteForEveryone(m, chat, myUid) {
  if (!m || m.senderId !== myUid) return false;
  if (m.systemType === 'call_history') return false;
  if (chat?.isGroup) return false;          // the app offers only "for me" on a single group message
  // 🟥 SEEN COMES FROM THE WATERMARK, NOT THE DOC. This first shipped reading
  //    m.seenBy/m.seen — the fields the APP consults, because the app maintains them.
  //    On the web they are almost always stale-false: the server never flips per-message
  //    flags, it advances chats/{id}.readUpTo[uid], and 1:1 docs are often swept before
  //    any flag could land. So a message whose ticks were sitting there BLUE — read,
  //    derived from that watermark by deriveReceiptStatus — still offered "delete for
  //    everyone". The eligibility now asks the exact function the ticks ask, so the
  //    sheet can never disagree with the checkmarks rendered next to the message.
  return deriveReceiptStatus(m, chat, myUid) !== 'read';
}

export async function deleteForMe(chatId, messageId, myUid) {
  try {
    await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
      deletedFor: arrayUnion(myUid),
    });
    return true;
  } catch {
    // The doc is already swept from the cloud (store-and-forward) — there is nothing to
    // mark. The caller hides it locally instead; that IS delete-for-me for a local copy.
    return false;
  }
}

export async function deleteForEveryone(chat, m, myUid) {
  const chatId = chat.id;
  const ref = doc(db, 'chats', chatId, 'messages', m.id);
  const deviceId = await getIdentity().then((i) => i.deviceId).catch(() => null);

  const tombstone = {
    type: 'text',
    text: 'Message deleted',
    isDeleted: true,
    imageUrl: deleteField(), videoUrl: deleteField(), audioUrl: deleteField(),
    documentUrl: deleteField(), location: deleteField(), latitude: deleteField(),
    longitude: deleteField(), contactName: deleteField(), contactPhoneNumbers: deleteField(),
    flightData: deleteField(), images: deleteField(), linkMetadata: deleteField(),
    replyTo: deleteField(),
    deletedByUid: myUid,
    deletedByDeviceId: deviceId || null,
  };

  try {
    await updateDoc(ref, tombstone);
  } catch (e) {
    // Swept already — recreate the tombstone so every device still learns of the delete.
    // Same shape the app writes in its not-found branch (ChatModal.tsx:9895).
    await setDoc(ref, {
      type: 'text', text: 'Message deleted', isDeleted: true, deleted: true,
      senderId: myUid, timestamp: m.timestamp || serverTimestamp(),
      deletedByUid: myUid, deletedByDeviceId: deviceId || null,
    }, { merge: true }).catch(() => {});
  }

  // 🟥 THE EMIT IS THE NOTIFICATION PULL. Without it the delete still lands via
  //    Firestore, but only whenever the other side next opens the chat — and the
  //    original push stays sitting on their lock screen, message intact. The server's
  //    handler is what cancels it (index.ts:797 on their phone does the actual pull).
  try {
    const s = getSocket();
    if (s) {
      s.emit('delete-message', {
        chatId,
        messageId: m.id,
        toUserId: chat.isGroup ? null : (chat.participants || []).find((u) => u !== myUid) || null,
        participantIds: chat.isGroup ? (chat.participants || []) : null,
      });
    }
  } catch { /* the tombstone is written either way */ }
}

/**
 * 🧩 ONE ITEM OF AN ALBUM — the app's protocol (ChatModal removeAlbumItem):
 *   for me        `hiddenItemsFor.<myUid>: arrayUnion(index)` — my devices stop showing it
 *   for everyone  `removedImageIndices: arrayUnion(index)` — nobody sees it; its file goes
 * plus `itemsEditedAt`, which is how a phone that already has the album learns of it. The
 * album's arrays are never rewritten. When nothing is left, the whole message goes the
 * ordinary way. Returns 'item' | 'message'.
 */
export async function removeAlbumItem(chat, m, index, myUid, scope) {
  const patch = albumItemRemoval(m, index, scope, myUid);
  if (albumEmptyFor({ ...m, ...patch }, myUid)) {
    if (scope === 'everyone') await deleteForEveryone(chat, m, myUid);
    else await deleteForMe(chat.id, m.id, myUid);
    return 'message';
  }
  const ref = doc(db, 'chats', chat.id, 'messages', m.id);
  if (scope === 'everyone') {
    const url = Array.isArray(m.images) ? m.images[index] : null;
    await updateDoc(ref, { removedImageIndices: arrayUnion(index), itemsEditedAt: serverTimestamp() });
    if (typeof url === 'string' && /^https?:/.test(url)) deleteObject(storageRef(storage, url)).catch(() => {});
  } else {
    await updateDoc(ref, { [`hiddenItemsFor.${myUid}`]: arrayUnion(index), itemsEditedAt: serverTimestamp() });
  }
  return 'item';
}

/** Note-to-Self / AI chats: the app erases outright — there is nobody else to inform. */
export async function eraseCompletely(chatId, messageId) {
  try { await deleteDoc(doc(db, 'chats', chatId, 'messages', messageId)); } catch { /* gone */ }
}

/** If the newest message was deleted for everyone, the chat row must say so too. */
export async function tombstonePreview(chatId) {
  try {
    await updateDoc(doc(db, 'chats', chatId), {
      lastMessage: 'Message deleted',
      lastMessageSystem: { type: 'deleted' },
    });
  } catch { /* preview only */ }
}


/* ═══════════════════════════════════════════════════════════════════════════
   👍 THE CHAT LIST HEARS ABOUT A REACTION — the same chat-document write the phone
   makes (ChatModal addReactionToMessage, step 2c), so a reaction made in the
   browser moves the chat to the top and reads "You reacted ❤️ to …" on every
   device, and removing it puts the row back.
     · add    → the marker + `lastEventAt` (NOT `timestamp`, which stays the newest
                message's time) + an unread for everyone else, as the phone does.
     · remove → only when the row is describing THIS reaction: back to the newest
                message, a text one as the encrypted placeholder every send writes.
   🔒 A TEXT target's words are never written: the chat document is shared and
      unencrypted. Each device quotes its own copy by `targetMessageId`.
   ═══════════════════════════════════════════════════════════════════════════ */
const KIND_LINE = {
  photo: 'a photo', video: 'a video', voice: 'a voice message', location: 'a location',
  document: 'a document', poll: 'a poll', post: 'a shared post', photos: 'multiple photos',
};
export async function writeReactionPreview({ chat, message, emoji, removing, myUid, myName, newest }) {
  if (!chat?.id || !message?.id || !emoji || !myUid) return;
  const ref = doc(db, 'chats', chat.id);
  if (!removing) {
    const kind = reactionTargetKind(message);
    const target = KIND_LINE[kind] || '';
    const left = Array.isArray(chat.leftParticipants) ? chat.leftParticipants : [];
    const unread = {};
    (chat.participants || []).forEach((u) => {
      if (u && u !== myUid && !left.includes(u)) unread[`unreadCounts.${u}`] = increment(1);
    });
    await updateDoc(ref, {
      lastMessage: `Reacted ${emoji} to: "${target}"`,
      lastMessageSystem: {
        type: 'reaction', emoji, name: myName || 'Someone', targetText: target,
        targetMessageId: String(message.id), targetKind: kind,
      },
      lastEventAt: serverTimestamp(),
      lastMessageSenderId: myUid,
      ...unread,
    });
    return;
  }
  const sys = chat.lastMessageSystem;
  if (!(sys?.type === 'reaction' && (!sys.targetMessageId || String(sys.targetMessageId) === String(message.id)))) return;
  const to = newest || message;
  const line = to.type === 'text'
    ? (chat.isGroup ? '🔒 [Encrypted Group Message]' : '🔒 Encrypted message')
    : quoteTextFor(to);
  await updateDoc(ref, {
    lastMessage: line,
    lastMessageSystem: null,
    ...(to.senderId ? { lastMessageSenderId: to.senderId } : {}),
  });
}
